import { axiosClient } from '@/shared/apis/config';
import { ApiResponse } from '@/shared/types';
import { CustomQueryOptions } from '@/shared/types/global/react-query/query';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { DtoContentType, DtoProjectContentType } from '@/shared/constants';

// ============================================================
// Helper Functions
// ============================================================

/**
 * 배열의 첫 번째 요소를 반환합니다 (null safety)
 * 기존 싱글 응답을 받던 코드의 호환성을 위해 사용
 */
export const getFirst = <T>(items: T[] | undefined | null): T | null =>
  items?.[0] ?? null;

// ============================================================
// Request DTOs
// ============================================================

export const GetGtsContentsV2RequestSchema = z.object({
  projectId: z.number(),
  type: z.nativeEnum(DtoProjectContentType),
  referenceId: z.number().optional(),
});

export type GetGtsContentsV2Request = z.infer<
  typeof GetGtsContentsV2RequestSchema
>;

export const GetGtsContentsBulkV2RequestSchema = z.object({
  projectId: z.number(),
  types: z.array(z.nativeEnum(DtoProjectContentType)),
});

export type GetGtsContentsBulkV2Request = z.infer<
  typeof GetGtsContentsBulkV2RequestSchema
>;

// ============================================================
// Response DTOs
// ============================================================

export const GtsContentDtoSchema = z.object({
  id: z.number(),
  contentType: z.nativeEnum(DtoContentType).nullable().optional(),
  publicUrl: z.string().nullable().optional(),
  thumbnailPublicUrl: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  fileSize: z.number().nullable().optional(),
  duration: z.number().nullable().optional(),
  width: z.number().nullable().optional(),
  height: z.number().nullable().optional(),
  projectContentType: z.nativeEnum(DtoProjectContentType).nullable().optional(),
  referenceId: z.number().nullable().optional(),
  createdAt: z.string().nullable().optional(),
});

export type GtsContentDto = z.infer<typeof GtsContentDtoSchema>;

// BE는 PageResponse<ProjectContentDto>를 반환 (data가 직접 배열)
export type GetGtsContentsV2ApiResponse = {
  status: string;
  code: number;
  message: string;
  data: GtsContentDto[];
  metaData: {
    totalCount: number;
    totalPages: number;
  };
};

export type GetGtsContentsBulkV2ApiResponse = {
  status: string;
  code: number;
  message: string;
  data: GtsContentDto[];
  metaData: {
    totalCount: number;
    totalPages: number;
  };
};

// ============================================================
// API Functions
// ============================================================

/**
 * v2.0 GTS 콘텐츠 목록 조회 (타입별)
 */
export const getGtsContentsV2Api = async (params: GetGtsContentsV2Request) => {
  const { projectId, type, referenceId } = params;

  const response = await axiosClient.get<GetGtsContentsV2ApiResponse>(
    `/v2.0/project/${projectId}/gts/contents`,
    {
      params: {
        types: [type],
        referenceId,
      },
    },
  );
  return response.data;
};

/**
 * v2.0 프로젝트 콘텐츠 목록 조회 (여러 타입)
 */
export const getGtsContentsBulkV2Api = async (
  params: GetGtsContentsBulkV2Request,
) => {
  const { projectId, types } = params;

  const response = await axiosClient.get<GetGtsContentsBulkV2ApiResponse>(
    `/v2.0/project/${projectId}/gts/contents`,
    {
      params: {
        types,
      },
    },
  );
  return response.data;
};

/**
 * v2.0 씬별 콘텐츠 목록 조회
 */
export const getGtsSceneContentsV2Api = async (
  projectId: number,
  sceneId: number,
) => {
  const response = await axiosClient.get<GetGtsContentsV2ApiResponse>(
    `/v2.0/project/${projectId}/gts/contents`,
    {
      params: {
        types: [DtoProjectContentType.SCENE],
        referenceId: sceneId,
      },
    },
  );
  return response.data;
};

// ============================================================
// Query Keys
// ============================================================

export const QueryKeyUseGetGtsContentsV2Api = 'GetGtsContentsV2Api' as const;
export const QueryKeyUseGetGtsContentsBulkV2Api =
  'GetGtsContentsBulkV2Api' as const;
export const QueryKeyUseGetGtsSceneContentsV2Api =
  'GetGtsSceneContentsV2Api' as const;

// ============================================================
// React Query Hooks
// ============================================================

/**
 * v2.0 GTS 콘텐츠 목록 조회 훅 (타입별)
 */
export const useGetGtsContentsV2Api = (
  params: GetGtsContentsV2Request,
  options?: CustomQueryOptions<GetGtsContentsV2ApiResponse>,
) => {
  const { queryKey, ...rest } = options ?? {};

  return useQuery({
    queryKey: [QueryKeyUseGetGtsContentsV2Api, params, ...(queryKey ?? [])],
    queryFn: async () => await getGtsContentsV2Api(params),
    ...rest,
  });
};

/**
 * v2.0 GTS 콘텐츠 목록 조회 훅 (여러 타입)
 */
export const useGetGtsContentsBulkV2Api = (
  params: GetGtsContentsBulkV2Request,
  options?: CustomQueryOptions<GetGtsContentsBulkV2ApiResponse>,
) => {
  const { queryKey, ...rest } = options ?? {};

  return useQuery({
    queryKey: [QueryKeyUseGetGtsContentsBulkV2Api, params, ...(queryKey ?? [])],
    queryFn: async () => await getGtsContentsBulkV2Api(params),
    ...rest,
  });
};

/**
 * v2.0 씬별 콘텐츠 목록 조회 훅
 */
export const useGetGtsSceneContentsV2Api = (
  projectId: number,
  sceneId: number,
  options?: CustomQueryOptions<GetGtsContentsV2ApiResponse>,
) => {
  const { queryKey, ...rest } = options ?? {};

  return useQuery({
    queryKey: [
      QueryKeyUseGetGtsSceneContentsV2Api,
      projectId,
      sceneId,
      ...(queryKey ?? []),
    ],
    queryFn: async () => await getGtsSceneContentsV2Api(projectId, sceneId),
    ...rest,
  });
};
